import {
  createSlice,
  Dispatch,
  PayloadAction,
  UnknownAction,
} from '@reduxjs/toolkit';

export const CHAT_ADVISOR_SLICE_FEATURE_KEY = 'chatAdvisorSlice';

/*
 * Update these interfaces according to your requirements.
 */

type feedbackData = {
  comment: null | string,
  feedback_id: string,
  feedback_type: string,
  sources?: { source_id: string; file_name?: string }[],
};

export interface Message {
  isUser: boolean
  isAdvisor: boolean
  message: string
  feedback_history: feedbackData[];
  comment_history: feedbackData[];
  isFeedbackAllowed: boolean
  messageId: string
  metaData: any
  comment: string;
  intent_and_entities?: any;
  advisor_name?: string;
  rephrased_query: string
  figure_refs?: string[]
  isStreaming?: boolean // Flag to indicate if this is a streaming message
  isHistorical?: boolean // Restored from conversation history; renders in full, never animated
  explainanswer: {
    answer: string,
    metaData: any,
    rephrased_query: string,
    reranked_context: any,
    retrieved_context: any,
    question?: string
  },
  aiResponseReceivedTime: string
  thinkingProcesses?: Array<{
    type: string;
    message: string;
    duration_sec?: number;
  }>
  is_ask?: boolean;
  multi_select?: boolean;
  clarifying_question_config?: any;
}
export interface ChatMessageUploadedImageInfo {
  source_id: string;
  file_name: string;
}
export type ChatSessionStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface ChatAdvisorSliceState {
  chatMessages: Message[],
  promptMessage?: string,
  selectedMessage?: string,
  currentActiveSocketObject: { [key: string]: any },
  isSocketLoading: boolean,
  socketError: any
  currentSocketListener: { [key: string]: any }
  currentChatMessageUploadedImageInfo: ChatMessageUploadedImageInfo,
  uploadedImageData: string | null
  currentChatMentionedEntities: any
  showInactivityModal: boolean
  isStreamingInProgress: boolean // Add this flag for REST streaming
  agentMessageState?: { [messageId: string]: { comments: feedbackData[], feedback: string } } // Store agent message state
  sessions: Record<string, ChatSessionState>
  activeSessionId: string | null
  focusedSessionId: string | null
  conversationsSidebar: {
    scrollTop: number
    selectedSessionId: string | null
    isCollapsed: boolean
  }
}

export interface ChatSessionState extends Omit<ChatAdvisorSliceState, 'sessions' | 'activeSessionId' | 'focusedSessionId' | 'conversationsSidebar'> {
  sessionId: string
  /** Thread id the chat APIs post to; equal to sessionId. */
  senderId?: string
  advisorId?: string
  title?: string
  updatedAt?: string
  status: ChatSessionStatus
  view?: Record<string, any>
}



export const initialChatAdvisorSliceState: ChatAdvisorSliceState = {
  chatMessages: [],
  currentActiveSocketObject: {},
  isSocketLoading: false,
  socketError: {},
  currentSocketListener: {},
  currentChatMessageUploadedImageInfo: {
    source_id: '',
    file_name: ''
  },
  uploadedImageData: null,
  currentChatMentionedEntities: {},
  showInactivityModal: false,
  isStreamingInProgress: false, // Initialize as false
  agentMessageState: {}, // Initialize agent message state
  sessions: {},
  activeSessionId: null,
  focusedSessionId: null,
  conversationsSidebar: {
    scrollTop: 0,
    selectedSessionId: null,
    // Collapsed until asked for: the chat gets the full width by default.
    isCollapsed: true,
  },
}

const createSession = (sessionId: string): ChatSessionState => ({
  sessionId,
  // Sessions are keyed by the sender_id that binds a conversation's turns
  // together, so the key is the sender_id unless hydration overrides it.
  senderId: sessionId,
  chatMessages: [],
  currentActiveSocketObject: {},
  isSocketLoading: false,
  socketError: {},
  currentSocketListener: {},
  currentChatMessageUploadedImageInfo: { source_id: '', file_name: '' },
  uploadedImageData: null,
  currentChatMentionedEntities: {},
  showInactivityModal: false,
  isStreamingInProgress: false,
  agentMessageState: {},
  status: 'idle',
  view: {},
});

const sessionIdOf = (state: ChatAdvisorSliceState, action: any): string | null =>
  action?.meta?.sessionId ?? action?.payload?.sessionId ?? state.focusedSessionId ?? state.activeSessionId;

const payloadOf = (action: any) =>
  action?.payload && typeof action.payload === 'object' && 'value' in action.payload
    ? action.payload.value
    : action.payload;

/** Bind every asynchronous chat update to one session, independent of focus changes. */
export const createChatSessionDispatch = (dispatch: Dispatch, sessionId: string): Dispatch =>
  ((action: UnknownAction & { payload?: unknown }) => dispatch({
    ...action,
    meta: { ...((action as any).meta ?? {}), sessionId },
  })) as Dispatch;

const sessionOf = (state: ChatAdvisorSliceState, action: any): ChatSessionState | null => {
  const sessionId = sessionIdOf(state, action);
  if (!sessionId) return null;
  state.sessions[sessionId] ??= createSession(sessionId);
  return state.sessions[sessionId];
};

const projectActiveSession = (state: ChatAdvisorSliceState, session: ChatSessionState | null) => {
  if (!session || session.sessionId !== state.activeSessionId) return;
  state.chatMessages = session.chatMessages;
  state.promptMessage = session.promptMessage;
  state.selectedMessage = session.selectedMessage;
  state.currentActiveSocketObject = session.currentActiveSocketObject;
  state.isSocketLoading = session.isSocketLoading;
  state.socketError = session.socketError;
  state.currentSocketListener = session.currentSocketListener;
  state.currentChatMessageUploadedImageInfo = session.currentChatMessageUploadedImageInfo;
  state.uploadedImageData = session.uploadedImageData;
  state.currentChatMentionedEntities = session.currentChatMentionedEntities;
  state.showInactivityModal = session.showInactivityModal;
  state.isStreamingInProgress = session.isStreamingInProgress;
  state.agentMessageState = session.agentMessageState;
};

export const chatAdvisorSliceSlice = createSlice({
  name: CHAT_ADVISOR_SLICE_FEATURE_KEY,
  initialState: initialChatAdvisorSliceState,
  reducers: {
    openChatSession: (state, action) => {
      const { sessionId, advisorId, title, updatedAt, view } = action.payload;
      const session = state.sessions[sessionId] ?? createSession(sessionId);
      Object.assign(session, {
        advisorId: advisorId ?? session.advisorId,
        title: title ?? session.title,
        updatedAt: updatedAt ?? session.updatedAt,
        view: { ...(session.view ?? {}), ...(view ?? {}) },
      });
      state.sessions[sessionId] = session;
      state.activeSessionId = sessionId;
      state.focusedSessionId = sessionId;
      state.conversationsSidebar.selectedSessionId = sessionId;
      projectActiveSession(state, session);
    },
    hydrateChatSession: (state, action) => {
      const { sessionId, senderId, messages = [], advisorId, title, updatedAt, view } = action.payload;
      const session = state.sessions[sessionId] ?? createSession(sessionId);
      if (session.chatMessages.length === 0) session.chatMessages = messages;
      Object.assign(session, {
        senderId: senderId ?? session.senderId,
        advisorId,
        title,
        updatedAt,
        view: { ...(session.view ?? {}), ...(view ?? {}) },
      });
      state.sessions[sessionId] = session;
      projectActiveSession(state, session);
    },
    setChatSessionStatus: (state, action) => {
      const session = sessionOf(state, action);
      if (!session) return;
      session.status = action.payload.status;
      session.socketError = action.payload.error ?? (action.payload.status === 'error' ? session.socketError : {});
      session.updatedAt = action.payload.updatedAt ?? new Date().toISOString();
      projectActiveSession(state, session);
    },
    updateConversationsSidebarView: (state, action) => {
      if (typeof action.payload?.scrollTop === 'number') {
        state.conversationsSidebar.scrollTop = action.payload.scrollTop;
      }
      if ('selectedSessionId' in (action.payload ?? {})) {
        state.conversationsSidebar.selectedSessionId = action.payload.selectedSessionId;
      }
      if (typeof action.payload?.isCollapsed === 'boolean') {
        state.conversationsSidebar.isCollapsed = action.payload.isCollapsed;
      }
    },
    updateUserMessage: (state, action) => {
      const session = sessionOf(state, action);
      const message = payloadOf(action);
      if (session) {
        session.chatMessages.push(message);
        session.updatedAt = new Date().toISOString();
        projectActiveSession(state, session);
      } else state.chatMessages.push(message)
    },
    updateCurrentSocketObject: (state, action) => {
      const session = sessionOf(state, action);
      if (session) {
        session.currentActiveSocketObject = payloadOf(action);
        projectActiveSession(state, session);
      } else state.currentActiveSocketObject = action.payload
    },
    updateMessageFeedback: (state, action) => {
      const session = sessionOf(state, action);
      const target = session ?? state;
      target.chatMessages = target.chatMessages?.map((message) => {
        const message_id = message?.metaData?.find((item:any)=>item?.message_id)?.message_id ?? ''
        if (message.isAdvisor && message_id === action.payload.messageId) {
          if ('feedback' in action.payload && action.payload.feedback !== undefined) {
            return { ...message, feedback: action.payload.feedback }

          } else if ('comment' in action.payload && action.payload.feedback !== undefined) {
            return { ...message, comment: action.payload.comment }
          }
        }
        return message
      })
      if (session) projectActiveSession(state, session);
    },
    clearChatMessages: (state, action) => {
      const session = sessionOf(state, action);
      const target = session ?? state;
      target.chatMessages = []
      target.currentActiveSocketObject = {}
      target.isSocketLoading = false
      if (session) projectActiveSession(state, session);
    },

    updateSocketLoading: (state, action) => {
      const session = sessionOf(state, action);
      const value = Boolean(payloadOf(action));
      if (session) {
        session.isSocketLoading = value;
        if (value) session.status = 'loading';
        else if (session.status === 'loading' && !session.isStreamingInProgress) {
          // 'ready' regardless of focus: the sidebar shows a check on any chat
          // that finished generating, and clears it when the next turn starts.
          session.status = 'ready';
          session.updatedAt = new Date().toISOString();
        }
        projectActiveSession(state, session);
      } else state.isSocketLoading = value
    },
    resetChatState: (state, action) => {
      const targetSessionId = sessionIdOf(state, action);
      if (targetSessionId) {
        state.sessions[targetSessionId] = createSession(targetSessionId);
        projectActiveSession(state, state.sessions[targetSessionId]);
      } else {
        state.chatMessages = []
        state.currentActiveSocketObject = {}
        state.isSocketLoading = false
      }
    },
    updatePromptMessage: (state, action) => {
      const session = sessionOf(state, action);
      if (session) {
        session.promptMessage = payloadOf(action);
        projectActiveSession(state, session);
      } else state.promptMessage = action.payload
    },
    updateSelectedMessage: (state, action) => {
      const session = sessionOf(state, action);
      if (session) {
        session.selectedMessage = payloadOf(action);
        projectActiveSession(state, session);
      } else state.selectedMessage = action.payload;
    },
    updateCurrentUploadedImageSourceId: (state, action) => {
      const session = sessionOf(state, action);
      if (session) {
        session.currentChatMessageUploadedImageInfo = payloadOf(action);
        projectActiveSession(state, session);
      } else state.currentChatMessageUploadedImageInfo = action.payload
    },
    updateUploadedImageData: (state, action) => {
      const session = sessionOf(state, action);
      if (session) {
        session.uploadedImageData = payloadOf(action);
        projectActiveSession(state, session);
      } else state.uploadedImageData = action.payload
    },
    updateCurrentChatMentionedEntities: (state, action) => {
      const session = sessionOf(state, action);
      if (session) {
        session.currentChatMentionedEntities = payloadOf(action);
        projectActiveSession(state, session);
      } else state.currentChatMentionedEntities = action.payload
    },
    updateSocketConnectionError: (state, action) => {
      const session = sessionOf(state, action);
      if (session) {
        session.socketError = payloadOf(action);
        session.status = 'error';
        projectActiveSession(state, session);
      } else state.socketError = action.payload
    },
    setShowInactivityModal: (state, action: PayloadAction<boolean>) => {
      const session = sessionOf(state, action);
      const value = Boolean(payloadOf(action));
      if (session) {
        session.showInactivityModal = value;
        projectActiveSession(state, session);
      } else state.showInactivityModal = value;
    },
    // Add new streaming actions for REST API approach
    setStreamingInProgress: (state, action: PayloadAction<boolean>) => {
      const session = sessionOf(state, action);
      const value = Boolean(payloadOf(action));
      if (session) {
        session.isStreamingInProgress = value;
        projectActiveSession(state, session);
      } else state.isStreamingInProgress = value;
    },
    removeLastAdvisorMessage: (state, action) => {
      const session = sessionOf(state, action);
      const target = session ?? state;
      // Find the last advisor message
      for (let i = target.chatMessages.length - 1; i >= 0; i--) {
        if (target.chatMessages[i].isAdvisor) {
          target.chatMessages.splice(i, 1);
          break;
        }
      }
      if (session) projectActiveSession(state, session);
    },
    startNewStreamedMessage: (state, action) => {
      const session = sessionOf(state, action);
      const target = session ?? state;
      const payload = payloadOf(action);
      // Create a new message for streaming
      target.chatMessages.push({
        ...payload,
        isStreaming: true,
        feedback: '',
        metaData: Array.isArray(payload.metaData) ? payload.metaData : [],
        messageId: '',
        rephrased_query: '',
        comment: '',
        explainanswer: payload.explainanswer || {
          answer: '',
          metaData: {},
          rephrased_query: '',
          reranked_context: {},
          retrieved_context: {}
        }
      });
      target.isStreamingInProgress = true;
      if (session) {
        session.status = 'loading';
        projectActiveSession(state, session);
      }
    },
    appendToStreamedMessage: (state, action) => {
      const session = sessionOf(state, action);
      const target = session ?? state;
      // Find the last message, which should be a streaming message
      const lastIndex = target.chatMessages.length - 1;
      if (lastIndex >= 0 && target.chatMessages[lastIndex].isStreaming) {
        // Append the new text to the existing message
        target.chatMessages[lastIndex].message += payloadOf(action);
      }
      if (session) projectActiveSession(state, session);
    },
    appendThinkingProcessToStreamedMessage: (state, action) => {
      const session = sessionOf(state, action);
      const target = session ?? state;
      // Find the last message, which should be a streaming message
      const lastIndex = target.chatMessages.length - 1;
      if (lastIndex >= 0 && target.chatMessages[lastIndex].isStreaming) {
        // Initialize thinkingProcesses array if it doesn't exist
        if (!target.chatMessages[lastIndex].thinkingProcesses) {
          target.chatMessages[lastIndex].thinkingProcesses = [];
        }
        // Append the new thinking process step
        target.chatMessages[lastIndex].thinkingProcesses.push(payloadOf(action));
      }
      if (session) projectActiveSession(state, session);
    },
    appendAgentPhaseToStreamedMessage: (state, action) => {
      const session = sessionOf(state, action);
      const target = session ?? state;
      // Find the last message, which should be a streaming message
      const lastIndex = target.chatMessages.length - 1;
      if (lastIndex >= 0 && target.chatMessages[lastIndex].isStreaming) {
        // Initialize phases array if it doesn't exist
        if (!(target.chatMessages[lastIndex] as any).phases) {
          (target.chatMessages[lastIndex] as any).phases = [];
        }
        // Append the new phase
        (target.chatMessages[lastIndex] as any).phases.push(payloadOf(action));
      }
      if (session) projectActiveSession(state, session);
    },
    completeStreamedMessage: (state, action) => {
      const session = sessionOf(state, action);
      const target = session ?? state;
      const payload = payloadOf(action);
      // Find the last message and update it with final metadata
      const lastIndex = target.chatMessages.length - 1;
      if (lastIndex >= 0 && target.chatMessages[lastIndex].isStreaming) {
        const existingMessage = target.chatMessages[lastIndex].message;
        const completionMessage = payload?.message;
        const hasExistingMessage = typeof existingMessage === 'string' && existingMessage.trim().length > 0;
        const hasValidCompletionMessage = typeof completionMessage === 'string' && completionMessage.trim().length > 0;

        target.chatMessages[lastIndex] = {
          ...target.chatMessages[lastIndex],
          ...(hasExistingMessage && !hasValidCompletionMessage
            ? { ...payload, message: existingMessage }
            : payload),
          isStreaming: false,
        };
        target.isStreamingInProgress = false;
        if (session) {
          session.isSocketLoading = false;
          session.status = 'ready';
          session.updatedAt = new Date().toISOString();
          projectActiveSession(state, session);
        }
      }
    },
    updateCommentHistory: (state, action) => {
      const session = sessionOf(state, action);
      const target = session ?? state;
      const message = target.chatMessages[action.payload.msgIndex];
      message?.comment_history?.push(action.payload.comment);
      if (session) projectActiveSession(state, session);
    },
    updateAgentMessageState: (state, action) => {
      const session = sessionOf(state, action);
      const target = session ?? state;
      // Consolidated action for agent message state (comments and feedback)
      if (!target.agentMessageState) {
        target.agentMessageState = {};
      }
      const { messageId, updates } = action.payload;
      if (!target.agentMessageState[messageId]) {
        target.agentMessageState[messageId] = { comments: [], feedback: '' };
      }

      // Replace comments if provided as array
      if (updates.comments) {
        target.agentMessageState[messageId].comments = updates.comments;
      }
      // Add single comment if provided
      else if (updates.comment) {
        target.agentMessageState[messageId].comments.push(updates.comment);
      }

      // Update feedback if provided
      if (updates.feedback) {
        target.agentMessageState[messageId].feedback = updates.feedback;
      }
      if (session) projectActiveSession(state, session);
    }
  }
});

/*
 * Export reducer for store configuration.
 */
export const chatAdvisorSliceReducer = chatAdvisorSliceSlice.reducer;
export const getMessageByIndex = (state: any, index: number) => {
  if (index < 0 || index > state.chatAdvisorSliceReducer.chatMessages.length) {
    return null;
  }

  return state?.chatAdvisorSliceReducer?.chatMessages[index - 1]?.message;
};
export const {
  openChatSession,
  hydrateChatSession,
  setChatSessionStatus,
  updateConversationsSidebarView,
  updateUserMessage,
  updateCurrentSocketObject,
  updateMessageFeedback,
  clearChatMessages,
  updateSocketLoading,
  resetChatState,
  updatePromptMessage,
  updateSelectedMessage,
  updateCurrentUploadedImageSourceId,
  updateUploadedImageData,
  updateCurrentChatMentionedEntities,
  updateSocketConnectionError,
  setShowInactivityModal,
  startNewStreamedMessage,
  appendToStreamedMessage,
  appendThinkingProcessToStreamedMessage,
  appendAgentPhaseToStreamedMessage,
  completeStreamedMessage,
  setStreamingInProgress,
  updateCommentHistory,
  updateAgentMessageState,
  removeLastAdvisorMessage
} = chatAdvisorSliceSlice.actions

